import React from 'react';
import { AMPEL_LABEL, type AmpelFarbe } from './planungAmpel';
import { AMPEL_PUNKT, AMPEL_TEXT, PLANUNGSLEISTUNG_OHNE_PHASE, hatHandlungsbedarf, kachelHinweisZeilen, quoteText } from './planungKachel';
import type { PlanungKachelStand } from './usePlanungKachel';

/**
 * Anzeige-Bausteine der Planungs-Ampel. Nur Komponenten: Farbtabellen und
 * Textbausteine stehen in planungKachel.ts (react-refresh/only-export-components).
 */

/** Der Ampelpunkt — gleich im Hub und im Dashboard. */
export const AmpelPunkt: React.FC<{ farbe: AmpelFarbe; className?: string; title?: string }> = ({ farbe, className = '', title }) => (
    <span title={title} className={`inline-block w-2 h-2 rounded-full shrink-0 ${AMPEL_PUNKT[farbe]} ${className}`} aria-hidden={title ? undefined : true} />
);

/**
 * Statuszeile der Hub-Kachel (Entscheid E12): Ampelpunkt, Label und
 * Klaerungsquote vorn — „● Handlungsbedarf · 7 von 32 geklärt".
 */
export const PlanungKachelStatus: React.FC<{ stand: PlanungKachelStand }> = ({ stand }) => {
    if (stand.laden) {
        return (
            <p className="text-[11px] font-semibold text-slate-400 flex items-center gap-1.5">
                <AmpelPunkt farbe="grau" /> Stand wird geladen …
            </p>
        );
    }
    if (stand.fehler) {
        return (
            <p className={`text-[11px] font-semibold flex items-center gap-1.5 ${AMPEL_TEXT.fehler}`}>
                <AmpelPunkt farbe="fehler" /> {AMPEL_LABEL.fehler}
            </p>
        );
    }
    // Inaktiv (Handy, SiFa): nichts zeichnen — die Kachel sieht aus wie bisher.
    if (!stand.ampel) return null;
    const { ampel } = stand;
    const quote = ampel.quote.gesamt > 0 ? ` · ${quoteText(ampel.quote)}` : '';
    return (
        <p className={`text-[11px] font-semibold flex items-center gap-1.5 ${AMPEL_TEXT[ampel.farbe]}`}>
            <AmpelPunkt farbe={ampel.farbe} />
            <span className="truncate">{AMPEL_LABEL[ampel.farbe]}{quote}</span>
        </p>
    );
};

/**
 * Hinweiszeilen unter dem Untertitel der Kachel: der Hinweis 1.6 („Planungs-
 * leistung beauftragt — Planungsphase noch nicht angelegt"), sonst der
 * Hauptgrund einer roten/gelben Ampel und darunter „Weiter: …" aus dem
 * Leitfaden (Entscheid E-L8, `kachelHinweisZeilen`), sonst der Lesefehler.
 * Nichts zu sagen → null, die Kachel sieht aus wie immer.
 */
export const PlanungKachelHinweis: React.FC<{ stand: PlanungKachelStand; lvHatPlanungsleistung: boolean }> = ({ stand, lvHatPlanungsleistung }) => {
    if (stand.laden) return null;
    if (stand.fehler) {
        return (
            <p className={`text-[11px] leading-snug ${AMPEL_TEXT.fehler}`} title={stand.fehler}>
                Planungsstand nicht lesbar: {stand.fehler}
            </p>
        );
    }
    if (!stand.ampel) return null;
    const farbe = stand.ampel.farbe;
    if (!stand.phaseVorhanden && lvHatPlanungsleistung) {
        // Die Farbe liefert die Ampel (gelb/rot); den Wortlaut legt der Hub fest.
        const ton = hatHandlungsbedarf(farbe) ? AMPEL_TEXT[farbe] : AMPEL_TEXT.gelb;
        return <p className={`text-[11px] font-semibold leading-snug ${ton}`}>{PLANUNGSLEISTUNG_OHNE_PHASE}</p>;
    }
    const zeilen = kachelHinweisZeilen(stand.ampel, stand.weiter);
    if (!zeilen.grund && !zeilen.weiter) return null;
    return (
        <>
            {zeilen.grund && <p className={`text-[11px] leading-snug truncate ${AMPEL_TEXT[farbe]}`}>{zeilen.grund}</p>}
            {zeilen.weiter && <p className="text-[11px] leading-snug truncate text-slate-600" title={zeilen.weiterVoll}>{zeilen.weiter}</p>}
        </>
    );
};
